/**
 * Carnet de ressources AMG Québec — données source unique.
 *
 * Phase 27.85 SSG — extraction des sections depuis carnet-content.tsx pour
 * permettre :
 * 1) Le composant React (modal + page) de consommer ces données
 * 2) Le Worker SSR (schema-org.ts + content-html-ssr.ts) d'enrichir le
 *    Schema.org ItemList ET d'injecter le HTML SSR dans <div id="root">
 *
 * Ces données sont 100% factuelles, sans claims AMF marketing. URLs vérifiées
 * 2026-06 vs sources officielles. Aucune affiliation, juste les bons numéros.
 */

export type CarnetLang = 'fr' | 'en';

export type CarnetEntry = {
  name: string;
  url: string;
  note: string;
};

export type CarnetSection = {
  id: string;
  title: string;
  intro: string;
  entries: CarnetEntry[];
};

const SECTIONS_FR: CarnetSection[] = [
  {
    id: 'carnet-regulateur',
    title: 'Régulateur & vérification',
    intro:
      "Au Québec, vendre de l'assurance maladie grave sans inscription à l'AMF est illégal. Vérifiez mon certificat avant même la première rencontre. C'est votre droit, et ça prend 30 secondes.",
    entries: [
      {
        name: 'AMF — Autorité des marchés financiers du Québec',
        url: 'https://lautorite.qc.ca',
        note: "Vérifier mon inscription (certificat n° 271218) ou celle de tout autre conseiller via la recherche par nom ou numéro. Régulateur officiel des conseillers en sécurité financière au Québec."
      },
      {
        name: 'ChAD — Chambre de la sécurité financière',
        url: 'https://www.chambresf.com',
        note: "Code de déontologie et discipline professionnelle des conseillers en sécurité financière. Permet de consulter les antécédents disciplinaires d'un conseiller."
      }
    ]
  },
  {
    id: 'carnet-assureurs',
    title: 'Assureurs canadiens',
    intro:
      "Les trois grands assureurs que je sollicite systématiquement pour chaque soumission comparative. Plus mon cabinet d'attache, iA. Vous pouvez consulter directement leurs produits AMG.",
    entries: [
      {
        name: 'Manuvie',
        url: 'https://www.manuvie.ca',
        note: "Un des trois assureurs canadiens majeurs en assurance maladie grave. Produits Lifecheque (capital forfaitaire) et garanties enfants."
      },
      {
        name: 'Canada-Vie',
        url: 'https://www.canadavie.com',
        note: "Produits Optima (AMG individuelle) et garanties optionnelles pour conjoint et enfants. Pricing distinct de Manuvie et Sun Life — d'où la comparaison."
      },
      {
        name: 'Sun Life',
        url: 'https://www.sunlife.ca',
        note: 'Produits Sun Critical Illness (capital forfaitaire 25 maladies couvertes). Conditions de pré-existence et clauses de retour des primes spécifiques.'
      },
      {
        name: 'iA Groupe financier',
        url: 'https://ia.ca',
        note: "Mon cabinet d'attache. Produits Acces vie & Tranquillité (AMG) avec options de remboursement de prime. Agence Beaubien (Montréal)."
      }
    ]
  },
  {
    id: 'carnet-soutien',
    title: 'Soutien après diagnostic',
    intro:
      "Si un diagnostic tombe avant que vous ayez la chance de souscrire une AMG, ces organismes offrent du soutien financier, psychologique et pratique. Aucune affiliation, juste les bons numéros.",
    entries: [
      {
        name: 'Société canadienne du cancer',
        url: 'https://cancer.ca',
        note: "Ligne d'information cancer (1 888 939-3333), programmes d'aide pratique (transport, hébergement), soutien aux aidants naturels et ressources francophones complètes."
      },
      {
        name: 'Fondation québécoise du cancer',
        url: 'https://fqc.qc.ca',
        note: 'Hébergement temporaire pendant les traitements (Hôtellerie Omer-Deserres à Montréal), services psychosociaux et aide financière directe aux familles québécoises.'
      },
      {
        name: 'Société canadienne de la SP',
        url: 'https://scsp.ca',
        note: "Pour la sclérose en plaques, une maladie grave couverte par la plupart des contrats AMG. Soutien, recherche et aide à la navigation du système."
      },
      {
        name: 'Cœur + AVC',
        url: 'https://www.coeuretavc.ca',
        note: "Pour les survivants de crise cardiaque ou d'AVC (deux des trois maladies les plus couvertes par les contrats AMG). Programmes de réadaptation et soutien."
      }
    ]
  },
  {
    id: 'carnet-gouv',
    title: 'Programmes gouvernementaux',
    intro:
      "Avant ou après une AMG, ces programmes peuvent combler une partie du manque à gagner. À connaître pour éviter de payer deux fois pour la même protection.",
    entries: [
      {
        name: 'AE — Prestations de maladie',
        url: 'https://www.canada.ca/fr/services/prestations/ae/assurance-emploi-maladie.html',
        note: "Assurance-emploi prestations spéciales de maladie : jusqu'à 26 semaines, ~55 % du salaire. À ne pas confondre avec l'AMG (qui est forfaitaire, pas un remplacement de revenu)."
      },
      {
        name: 'PCMP — Prestations canadiennes pour les proches aidants',
        url: 'https://www.canada.ca/fr/services/prestations/ae/proche-aidant.html',
        note: "Pour les conjoints ou parents qui suspendent leur emploi pour s'occuper d'un proche atteint. Jusqu'à 26 semaines de prestations."
      },
      {
        name: 'IVAC — Indemnisation des victimes',
        url: 'https://www.ivac.qc.ca',
        note: "Pour les rares cas où la maladie grave découle d'un acte criminel (rare mais existant). Peu de Québécois savent que ce programme existe."
      },
      {
        name: "Régie de l'assurance maladie du Québec (RAMQ)",
        url: 'https://www.ramq.gouv.qc.ca',
        note: "Programme d'aide aux personnes atteintes de cancer pour médicaments d'exception et soins hospitaliers. Informations sur la couverture publique."
      }
    ]
  },
  {
    id: 'carnet-information',
    title: 'Information & vulgarisation',
    intro:
      "Sources francophones indépendantes pour vérifier ce que je vous dis. Si quelque chose dans ma méthode vous inquiète, lisez ces sources avant de signer quoi que ce soit.",
    entries: [
      {
        name: 'Éducaloi',
        url: 'https://educaloi.qc.ca',
        note: 'Organisme québécois sans but lucratif qui vulgarise le droit. Section assurance et consommation très claire. Aucune affiliation commerciale.'
      },
      {
        name: 'Option Consommateurs',
        url: 'https://option-consommateurs.org',
        note: "Association québécoise de défense des consommateurs. Rapports indépendants sur les pratiques d'assurance et de finance personnelle au Québec."
      },
      {
        name: 'ACFC — Agence consommation finance Canada',
        url: 'https://www.canada.ca/fr/agence-consommation-matiere-financiere.html',
        note: "Régulateur fédéral de la protection des consommateurs en matière financière. Guides pratiques sur les contrats d'assurance."
      }
    ]
  },
  {
    id: 'carnet-recours',
    title: 'Recours & médiation',
    intro:
      "Si un jour vous estimez avoir été mal conseillé — par moi ou par n'importe quel autre conseiller — voici les voies officielles de recours. Je préfère que vous les connaissiez avant de signer plutôt qu'après.",
    entries: [
      {
        name: 'OAP — Ombudsman des assurances de personnes',
        url: 'https://www.oapcanada.ca',
        note: "Médiation indépendante et gratuite entre assurés et assureurs sur les produits d'assurance individuelle (vie, AMG, invalidité). Décisions non-contraignantes mais souvent respectées."
      },
      {
        name: "AMF — Direction de l'assistance et de l'indemnisation",
        url: 'https://lautorite.qc.ca/grand-public/assistance-et-plainte',
        note: "Pour porter plainte contre un conseiller, déclencher une enquête disciplinaire, ou obtenir réparation via le Fonds d'indemnisation des services financiers."
      },
      {
        name: 'TAQ — Tribunal administratif du Québec',
        url: 'https://www.taq.gouv.qc.ca',
        note: 'Pour contester certaines décisions administratives liées à des programmes publics (CNESST, IVAC, etc.). Procédure encadrée par avocat recommandée.'
      }
    ]
  }
];

const SECTIONS_EN: CarnetSection[] = [
  {
    id: 'carnet-regulateur',
    title: 'Regulator & verification',
    intro:
      'In Quebec, selling critical illness insurance without AMF registration is illegal. Verify my certificate before our first meeting. It is your right, and it takes 30 seconds.',
    entries: [
      {
        name: 'AMF — Autorité des marchés financiers (Quebec)',
        url: 'https://lautorite.qc.ca',
        note: "Verify my registration (certificate no. 271218) or any other advisor's via the name or number search. Official Quebec regulator for financial security advisors."
      },
      {
        name: 'ChAD — Chambre de la sécurité financière',
        url: 'https://www.chambresf.com',
        note: "Code of ethics and professional discipline for financial security advisors. Lets you consult an advisor's disciplinary history."
      }
    ]
  },
  {
    id: 'carnet-assureurs',
    title: 'Canadian insurers',
    intro:
      'The three major insurers I systematically solicit for every comparative quote, plus my affiliated firm iA. You can directly consult their critical illness products.',
    entries: [
      {
        name: 'Manulife',
        url: 'https://www.manulife.ca/en.html',
        note: 'One of the three major Canadian critical illness insurers. Lifecheque products (lump-sum capital) and child riders.'
      },
      {
        name: 'Canada Life',
        url: 'https://www.canadalife.com',
        note: 'Optima individual CI products and optional spousal and child riders. Distinct pricing from Manulife and Sun Life — hence the comparison.'
      },
      {
        name: 'Sun Life',
        url: 'https://www.sunlife.ca/en/',
        note: 'Sun Critical Illness products (25 covered illnesses). Pre-existing conditions and return-of-premium clauses are specific.'
      },
      {
        name: 'iA Financial Group',
        url: 'https://ia.ca/en',
        note: 'My affiliated firm. Acces Life & Tranquillité (CI) products with return-of-premium options. Beaubien Agency (Montréal).'
      }
    ]
  },
  {
    id: 'carnet-soutien',
    title: 'Post-diagnosis support',
    intro:
      'If a diagnosis arrives before you had a chance to take out critical illness coverage, these organizations offer financial, psychological and practical support. No affiliations, just the right numbers.',
    entries: [
      {
        name: 'Canadian Cancer Society',
        url: 'https://cancer.ca/en/',
        note: 'Cancer information line (1 888 939-3333), practical aid programs (transportation, lodging), caregiver support and full English resources.'
      },
      {
        name: 'Quebec Cancer Foundation',
        url: 'https://fqc.qc.ca/en/',
        note: 'Temporary lodging during treatment (Hôtellerie Omer-Deserres in Montreal), psychosocial services and direct financial aid for Quebec families.'
      },
      {
        name: 'MS Canada',
        url: 'https://mscanada.ca/en/',
        note: 'For multiple sclerosis, a serious illness covered by most CI contracts. Support, research and system navigation aid.'
      },
      {
        name: 'Heart & Stroke',
        url: 'https://www.heartandstroke.ca',
        note: 'For survivors of heart attack or stroke (two of the three most covered illnesses in CI contracts). Rehabilitation programs and support.'
      }
    ]
  },
  {
    id: 'carnet-gouv',
    title: 'Government programs',
    intro:
      'Before or after a CI policy, these programs may fill part of the income gap. Worth knowing to avoid paying twice for the same protection.',
    entries: [
      {
        name: 'EI — Sickness benefits',
        url: 'https://www.canada.ca/en/services/benefits/ei/ei-sickness.html',
        note: 'Employment Insurance sickness benefits: up to 26 weeks, ~55 % of salary. Not to be confused with CI (which is lump-sum, not income replacement).'
      },
      {
        name: 'Canadian Caregiving Benefit',
        url: 'https://www.canada.ca/en/services/benefits/ei/caregiving.html',
        note: 'For spouses or parents who pause work to care for a loved one with a serious illness. Up to 26 weeks of benefits.'
      },
      {
        name: 'IVAC — Crime victim compensation (Quebec)',
        url: 'https://www.ivac.qc.ca/Pages/Default.aspx',
        note: 'For the rare cases where the serious illness results from a criminal act. Few Quebecers know this program exists.'
      },
      {
        name: 'Quebec Health Insurance Board (RAMQ)',
        url: 'https://www.ramq.gouv.qc.ca/en',
        note: 'Aid program for people with cancer for exception medications and hospital care. Information on public coverage.'
      }
    ]
  },
  {
    id: 'carnet-information',
    title: 'Information & plain language',
    intro:
      'Independent sources to verify what I tell you. If anything in my method worries you, read these before signing.',
    entries: [
      {
        name: 'Éducaloi',
        url: 'https://educaloi.qc.ca/en/',
        note: 'Quebec nonprofit that simplifies legal language. Very clear insurance and consumer sections. No commercial affiliation.'
      },
      {
        name: 'Option Consommateurs',
        url: 'https://option-consommateurs.org/en/',
        note: 'Quebec consumer defense association. Independent reports on Quebec insurance and personal finance practices.'
      },
      {
        name: 'FCAC — Financial Consumer Agency of Canada',
        url: 'https://www.canada.ca/en/financial-consumer-agency.html',
        note: 'Federal regulator for financial consumer protection. Practical guides on insurance contracts.'
      }
    ]
  },
  {
    id: 'carnet-recours',
    title: 'Recourse & mediation',
    intro:
      'If one day you feel poorly advised — by me or by any other advisor — here are the official recourse paths. I prefer that you know them before signing rather than after.',
    entries: [
      {
        name: 'OLHI — Ombudsman for Life & Health Insurance',
        url: 'https://www.olhi.ca',
        note: 'Free independent mediation between policyholders and insurers on individual products (life, CI, disability). Non-binding but often respected decisions.'
      },
      {
        name: 'AMF — Assistance and indemnification',
        url: 'https://lautorite.qc.ca/en/general-public/assistance-and-complaint',
        note: 'To file a complaint against an advisor, trigger a disciplinary investigation, or obtain redress via the Financial Services Compensation Fund.'
      },
      {
        name: 'TAQ — Quebec Administrative Tribunal',
        url: 'https://www.taq.gouv.qc.ca',
        note: 'To contest certain administrative decisions related to public programs (CNESST, IVAC, etc.). Counsel-assisted procedure recommended.'
      }
    ]
  }
];

export function getCarnetSections(lang: CarnetLang): CarnetSection[] {
  return lang === 'fr' ? SECTIONS_FR : SECTIONS_EN;
}

export const CARNET_DISCLAIMER = {
  fr: "Les organismes listés ne sont pas affiliés à Océanne Brodeur ni à iA Groupe financier. Les liens externes sont fournis à titre de référence officielle ; les contenus restent sous la responsabilité de leurs éditeurs respectifs.",
  en: 'The organizations listed are not affiliated with Océanne Brodeur or iA Financial Group. External links are provided as official references; content remains the responsibility of their respective publishers.'
} as const;
